// Passo 1 · Origine: un repository che esiste già (PopTown lo clona nella cartella del progetto)
// oppure un progetto nuovo con nome e descrizione.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Field, TextArea, TextField } from '../../../components/Field';
import { Icon } from '../../../components/Icon';
import { Select } from '../../../components/Select';
import { api } from '../../../lib/api';
import type { GitRegistry, GitRepo, ProjectInput } from '../../../lib/api';
import { RepoPicker } from '../RepoPicker';

export type Origin = 'import' | 'new';

function Choice({
  on,
  icon,
  title,
  hint,
  onPick,
  children,
}: {
  on: boolean;
  icon: string;
  title: string;
  hint: string;
  onPick: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="np-choice" data-on={on || undefined}>
      <button type="button" className="np-choice-head" aria-pressed={on} onClick={onPick}>
        <Icon name={icon} size={20} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="t-strong" style={{ display: 'block' }}>
            {title}
          </span>
          <span className="t-caption dim">{hint}</span>
        </span>
        <span className="np-radio" />
      </button>
      {on && <div style={{ display: 'grid', gap: 12 }}>{children}</div>}
    </section>
  );
}

export function StepOrigin({
  origin,
  onOrigin,
  form,
  set,
  registries,
}: {
  origin: Origin;
  onOrigin: (o: Origin) => void;
  form: ProjectInput;
  set: (patch: Partial<ProjectInput>) => void;
  registries: GitRegistry[];
}) {
  const [repos, setRepos] = useState<GitRepo[] | null>(null);
  const registry = registries.find((r) => r.id === form.git?.registry) ?? null;

  useEffect(() => {
    setRepos(null);
    if (!registry || registry.kind === 'git') return;
    api.gitRepos(registry.id).then(
      (r) => setRepos(r.repos),
      () => setRepos([]),
    );
  }, [registry]);

  const details = (
    <>
      <Field label="Nome">
        <TextField value={form.name} onChange={(v) => set({ name: v })} />
      </Field>
      <Field label="Descrizione" hint="A cosa serve: la leggono PM e Team leader.">
        <TextArea value={form.description} onChange={(v) => set({ description: v })} rows={3} />
      </Field>
    </>
  );

  return (
    <div className="np-two">
      <Choice
        on={origin === 'import'}
        icon="git-fork"
        title="Importa da git"
        hint="Un repository che esiste già: PopTown lo clona."
        onPick={() => {
          onOrigin('import');
          if (!form.git && registries[0]) set({ git: { registry: registries[0].id, repo: '' } });
        }}
      >
        {registries.length === 0 ? (
          <div className="t-small dim">
            Nessun server git: si aggiunge nella pagina{' '}
            <Link to="/git" style={{ color: 'var(--accent)' }}>
              Git
            </Link>
            .
          </div>
        ) : (
          <>
            <Field label="Server git">
              <Select
                label="Server git"
                value={form.git?.registry ?? ''}
                onChange={(v) => set({ git: { registry: v, repo: '' } })}
                options={registries.map((r) => ({ value: r.id, label: r.name }))}
                width="100%"
              />
            </Field>
            {registry && (
              <Field label="Repository">
                <RepoPicker
                  registry={registry}
                  repos={repos}
                  value={form.git?.repo ?? ''}
                  onChange={(repo, branch) => {
                    set({
                      git: { registry: registry.id, repo },
                      ...(branch ? { baseBranch: branch } : {}),
                      ...(form.name ? {} : { name: repo.split('/').pop() ?? repo }),
                    });
                  }}
                />
              </Field>
            )}
            <Field label="Branch di partenza">
              <TextField mono value={form.baseBranch} onChange={(v) => set({ baseBranch: v })} />
            </Field>
            {details}
          </>
        )}
      </Choice>
      <Choice
        on={origin === 'new'}
        icon="plus"
        title="Nuovo progetto"
        hint="Parti da zero con nome e descrizione."
        onPick={() => {
          onOrigin('new');
          set({ git: null });
        }}
      >
        {details}
      </Choice>
    </div>
  );
}
